<template>
  <v-dialog v-model="localShowValue" persistent scrollable>
    <v-card>
      <v-card-title>
        <h3 class="withBackground">Key Field Comparison since Last Accepted Inspection</h3>
      </v-card-title>
      <v-card-text>
        <FieldComparison showPerformedData :moduleType="MODULE_TYPES.INSPECTION"> </FieldComparison>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-row>
          <v-col class="align-center">
            <v-btn variant="outlined" @click="handleSubmit(true)">{{
              connectivity.getOnlineServiceStatus ? "Submit" : "Submit Later"
            }}</v-btn>
            <v-btn variant="outlined" @click="closePopup">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-card-actions></v-card
    >
  </v-dialog>

  <ValidationResponse
    :show="showValidation"
    :validationErrors="getErrors"
    @close="showValidation = false"
    @resubmit="handleSubmit(false)"
    @reloadNotesComments="reloadNotesComments"
  ></ValidationResponse>
</template>
<script setup>
import { syncAllDirtyRecords } from "@/util/syncInterval";
import { ref, toRefs, watch, computed, onMounted } from "vue";
import { useStructureIDBStore } from "@/stores/structureIDB";
import { useInspectionStore } from "@/stores/inspection";
import { useConnectivityStore } from "@/stores/connectivity";
import FieldComparison from "@/components/shared/FieldComparison.vue";
import ValidationResponse from "@/components/shared/ValidationResponse.vue";
import { clone } from "@/util/clone";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { SUBMISSION_FAILURE, SUBMISSION_SUCCESS } from "@/constants/CommonWebConstants";
import { HTTP_STATUS_CODE } from "@/constants/CommonWebCodes";
import { useSnackbarStore } from "@/stores/snackbar";
import { SNACKBAR_MESSAGE_TYPES } from "@/constants/GlobalSnackbar.js";
import { LOGGER } from "@/util/logger";
import { useUserPermissionStore } from "@/stores/userPermission";
import { MODULE_TYPES } from "@/constants/InspectionConstants";

const props = defineProps({
  show: { default: false },
  brkey: { default: {} },
});

const emit = defineEmits(["close", "reloadNotesComments", "isSubmitting"]);

let { show } = toRefs(props);
let localShowValue = ref(show.value);

const structureIDBStore = useStructureIDBStore();
const inspectionStore = useInspectionStore();
const snackbarStore = useSnackbarStore();
const userPermissionStore = useUserPermissionStore();
const connectivity = useConnectivityStore();
let showValidation = ref(false);
const structure = computed(() => inspectionStore.selectedInspection);
let showSubmitSuccessMessageId = ref("");
let showSubmitLaterMessageId = ref("");
let warningSubmitFailMessageId = ref("");

const resetSnackBarMessage = () => {
  showSubmitSuccessMessageId.value = "";
  showSubmitLaterMessageId.value = "";
  warningSubmitFailMessageId.value = "";
};

const handleSubmit = async (isForValidation) => {
  emit('isSubmitting', true)
  closePopup();
  if (connectivity.getOnlineServiceStatus) {
    await submit(isForValidation);
  } else {
    await userPermissionStore.updateStructurePermission(structure.value);
    //change status
    structure.value.inspection.inspectionStatus = INSPECTION_STATUSES.READY_TO_SUBMIT;
    //Save the submitted structure to DB
    await structureIDBStore.saveStructure(structure.value);
    inspectionStore.setDirtyFlag(false);
    inspectionStore.setUnsavedChangesWarning(false);
    inspectionStore.setShowSubmitLaterMessage(true);
    showSubmitLaterMessageId.value = snackbarStore.showMessage({
      displayText: `The inspection has been marked '0 - Ready to Submit'. You can submit later
    from the Worklist when you are connected to the internet.`,
      timeout: 10000,
      messageType: SNACKBAR_MESSAGE_TYPES.WARNING,
      callback: resetSnackBarMessage,
    });
  }
  emit('isSubmitting', false)  
};

const submit = async (isForValidation) => {
  try {
    inspectionStore.userAuthenticationErrors = {};
    //Sync All dirty records
    await syncAllDirtyRecords();
    //Submit local structures
    const insp = await structureIDBStore.getLatestInspectionFromIDB(props.brkey);
    //add message to the insp object for submission/validation
    let submissionPayload = clone(insp);
    await inspectionStore.submitInspection(submissionPayload, isForValidation);
    if (getErrors.value?.length > 0) {
      //check if any errors
      if (
        getErrors.value.includes(HTTP_STATUS_CODE.BAD_REQUEST) ||
        getErrors.value.includes(HTTP_STATUS_CODE.SERVICE_UNAVAILABLE) ||
        getErrors.value.includes(HTTP_STATUS_CODE.SERVER_ERROR) ||
        getErrors.value.includes(
          HTTP_STATUS_CODE.GATEWAY_TIMEOUT || getErrors.value.includes(HTTP_STATUS_CODE.UNPROCESSABLE_ENTITY),
        )
      ) {
        warningSubmitFailMessageId.value = snackbarStore.showMessage({
          displayText: SUBMISSION_FAILURE.INSPECTION_FAILURE,
          timeout: 10000,
          messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
          callback: resetSnackBarMessage,
        });
      } else {
        showValidation.value = true;
        insp.inspection.inspectionStatus = INSPECTION_STATUSES.VALIDATION_ERROR;
        await saveToIDB(insp);
        structure.value.inspection.inspectionStatus = insp.inspection.inspectionStatus;
      }
    } else {
      insp.submittedTimestamp = new Date().toISOString();
      insp.inspection.inspectionStatus = INSPECTION_STATUSES.SUBMITTED;
      //Save the submitted structure to DB
      await saveToIDB(insp);
      structure.value.submittedTimestamp = insp.submittedTimestamp;
      structure.value.inspection.inspectionStatus = insp.inspection.inspectionStatus;

      //close the login if there is no authentication error in the first record
      showSubmitSuccessMessageId.value = snackbarStore.showMessage({
        displayText: SUBMISSION_SUCCESS,
        timeout: 10000,
        messageType: SNACKBAR_MESSAGE_TYPES.SUCCESS,
        callback: resetSnackBarMessage,
      });
    }
  } catch (e) {
    warningSubmitFailMessageId.value = snackbarStore.showMessage({
      displayText: SUBMISSION_FAILURE.INSPECTION_FAILURE,
      timeout: 10000,
      messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
      callback: resetSnackBarMessage,
    });
    LOGGER.logException(e);
  }
};

const saveToIDB = async (inspection) => {
  await userPermissionStore.updateStructurePermission(inspection);
  await structureIDBStore.saveStructure(inspection);
  inspectionStore.setDirtyFlag(false);
  inspectionStore.setUnsavedChangesWarning(false);
};

const closePopup = () => {
  localShowValue.value = false;
  emit("close");
};
const reloadNotesComments = () => {
  emit("reloadNotesComments");
};

const getErrors = computed(() => {
  return inspectionStore?.getSubmissionErrors()?.get(Number.parseInt(props.brkey));
});
onMounted(async () => {
  await structureIDBStore.initDB(false);
});

watch(show, (newValue) => {
  localShowValue.value = newValue;
});
</script>
